import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { type EventLogType } from '@/api/eventLog';
import Edit from '@/assets/svg/edit.svg?react';
import Delete from '@/assets/svg/trashcan.svg?react';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import { MoreActionsCell } from '@/components/MoreActionsCell';
import { USDateViewFormat } from '@/constants/formats';

enum ActionKey {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

type Props = {
  isOpen?: boolean;
  data?: EventLogType[];
  isLoading?: boolean;
  error: Error | null;
  selectedItem?: EventLogType;
  onSelectionChange?: (note?: EventLogType) => void;
  onNoteEdit: (note: EventLogType) => void;
  onNoteDelete: (note: EventLogType) => void;
};

const NotesPanelDrawerTable = ({
  data,
  isLoading,
  error,
  selectedItem,
  onSelectionChange,
  onNoteEdit,
  onNoteDelete,
}: Props) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!selectedItem) {
      return;
    }

    const selectedRowEl = tableWrapperRef.current?.querySelector<HTMLTableRowElement>(
      'tr[data-selected-row="true"]',
    );
    if (selectedRowEl) {
      selectedRowEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedItem, data?.length]);

  const handleActionSelect = useCallback(
    (key: ActionKey, note: EventLogType) => {
      if (key === ActionKey.EDIT) {
        onNoteEdit(note);
      } else if (key === ActionKey.DELETE) {
        onNoteDelete(note);
      }
    },
    [onNoteEdit, onNoteDelete],
  );

  const columns = useMemo(
    () =>
      [
        {
          id: 'date',
          header: t`Date`,
          size: 100,
          enableSorting: false,
          cell: ({ row }) => dayjs(row.original.date).utc().format(USDateViewFormat),
        },
        {
          id: 'rawDetails',
          header: t`Details`,
          size: 260,
          flex: 1,
          enableSorting: false,
          cell: ({ row }) => row.original.rawDetails,
        },
        {
          id: 'source',
          header: t`Source`,
          size: 140,
          flex: 1,
          enableSorting: false,
          cell: ({ row }) => row.original.source,
        },
        {
          id: 'actions',
          header: t`Actions`,
          size: 72,
          enableSorting: false,
          cell: ({ row }) =>
            row.original.type === 'Note' && (
              <MoreActionsCell<ActionKey>
                size="small"
                menuItems={[
                  {
                    key: ActionKey.EDIT,
                    label: t`Edit`,
                    icon: <Edit />,
                  },
                  {
                    key: ActionKey.DELETE,
                    label: t`Delete`,
                    danger: true,
                    icon: <Delete />,
                  },
                ]}
                onSelect={(key) => handleActionSelect(key, row.original)}
              />
            ),
        },
      ] satisfies DataTableColumnDef<EventLogType>[],
    [handleActionSelect],
  );

  return (
    <div
      ref={tableWrapperRef}
      data-testid="notes-table"
      className="max-h-[145px] w-full overflow-auto"
    >
      <DataTable<EventLogType>
        className="bg-white"
        columns={columns}
        data={data ?? []}
        size="small"
        loading={isLoading}
        error={error}
        emptyMessage={t`No notes were found.`}
        showHeader={false}
        pagination={false}
        getRowId={(record) => record.id}
        onRow={(record) => {
          const isSelectedRow = record.id === selectedItem?.id;

          return {
            className: isSelectedRow ? '[&_[data-slot=table-cell]]:bg-green-400/30' : undefined,
            'data-selected-row': isSelectedRow ? 'true' : undefined,
            onMouseEnter: () => onSelectionChange?.(record),
            onMouseLeave: () => onSelectionChange?.(undefined),
          };
        }}
      />
    </div>
  );
};

export default NotesPanelDrawerTable;
